import { Helmet } from "react-helmet-async";

const SITE_NAME = "Baseline Breakdown";
const SITE_DESCRIPTION =
  "Baseline Breakdown — editorial tennis magazine covering ATP, WTA, Grand Slams, coaching, and gear. Longform articles, the weekly Baseline Breakdown Podcast, and original digital content.";
const SITE_ORIGIN = "https://baselinebreakdown.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.png`;
const LOGO_IMAGE = `${SITE_ORIGIN}/android-chrome-512x512.png`;

export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  publishedAt,
  author,
  section,
  tags = [],
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Tennis Magazine`;
  const desc = description || SITE_DESCRIPTION;
  const canonicalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const img = image || DEFAULT_IMAGE;
  const isArticle = type === "article";

  // JSON-LD structured data — either Article or WebSite.
  const jsonLd = isArticle
    ? {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: title,
        description: desc,
        image: [img],
        datePublished: publishedAt,
        dateModified: publishedAt,
        author: author ? [{ "@type": "Person", name: author }] : undefined,
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          logo: { "@type": "ImageObject", url: LOGO_IMAGE },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        articleSection: section,
        keywords: tags.length ? tags.join(", ") : undefined,
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: canonicalUrl,
        description: SITE_DESCRIPTION,
        potentialAction: {
          "@type": "SearchAction",
          target: `${typeof window !== "undefined" ? window.location.origin : ""}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      };

  // Strip undefined keys before stringify.
  const cleanJsonLd = JSON.stringify(jsonLd, (_, v) => (v === undefined ? undefined : v));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {tags.length > 0 && <meta name="keywords" content={tags.join(", ")} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="en_US" />

      {isArticle && publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {isArticle && author && <meta property="article:author" content={author} />}
      {isArticle && section && <meta property="article:section" content={section} />}
      {isArticle && tags.map((t) => <meta key={t} property="article:tag" content={t} />)}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {isArticle && author && <meta name="twitter:label1" content="Written by" />}
      {isArticle && author && <meta name="twitter:data1" content={author} />}

      {/* Crawler hints */}
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      <meta name="googlebot" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />

      {/* Structured data */}
      <script type="application/ld+json">{cleanJsonLd}</script>
    </Helmet>
  );
}
